import api from '@/helpers/api'

/**
 * Vuex module untuk Dashboard Analytics (8 widget endpoints).
 *
 * Pattern: request/success/error sesuai standar frontend architecture.
 * Setiap action return Promise supaya komponen bisa await dan tahu kapan selesai.
 *
 * Cache di backend (Redis 5 menit) — frontend tidak perlu cache tambahan,
 * cukup simpan state terakhir dan re-fetch saat timeframe/filter berubah.
 */

const BASE_URL = '/api/dashboard/analytics'

export default {
  namespaced: true,

  state: {
    status: '',
    errMessage: '',

    // KPI Cards
    kpi: null,

    // Member Growth chart + recent members ticker
    memberGrowth: null,

    // Channel Ranking donut + source list
    channelRanking: null,

    // Point Activity chart
    pointActivity: null,

    // Tier Distribution bar chart
    tierDistribution: null,

    // Top Products
    topProducts: null,

    // Top Vouchers
    topVouchers: null,

    // Members at Risk
    membersAtRisk: null,

    // Filter aktif (untuk keperluan re-fetch atau display)
    activeFilter: {
      timeframe: 'this_month',
      corporate_id: null,
      branch_id: null,
      merchant_id: null,
    },
  },

  mutations: {
    request(state) {
      state.status = 'loading'
      state.errMessage = ''
    },
    success(state) {
      state.status = 'success'
    },
    error(state, { message }) {
      state.status = 'error'
      state.errMessage = message
    },
    setKpi(state, { data }) {
      state.kpi = data
    },
    setMemberGrowth(state, { data }) {
      state.memberGrowth = data
    },
    setChannelRanking(state, { data }) {
      state.channelRanking = data
    },
    setPointActivity(state, { data }) {
      state.pointActivity = data
    },
    setTierDistribution(state, { data }) {
      state.tierDistribution = data
    },
    setTopProducts(state, { data }) {
      state.topProducts = data
    },
    setTopVouchers(state, { data }) {
      state.topVouchers = data
    },
    setMembersAtRisk(state, { data }) {
      state.membersAtRisk = data
    },
    setActiveFilter(state, { filter }) {
      state.activeFilter = { ...state.activeFilter, ...filter }
    },
    clearAll(state) {
      state.kpi = null
      state.memberGrowth = null
      state.channelRanking = null
      state.pointActivity = null
      state.tierDistribution = null
      state.topProducts = null
      state.topVouchers = null
      state.membersAtRisk = null
      state.status = ''
      state.errMessage = ''
    },
  },

  actions: {
    /**
     * Fetch semua widget sekaligus. Dipakai saat pertama kali load atau saat filter berubah.
     * Parallel fetch — semua request jalan bersamaan (tidak blocking satu sama lain).
     */
    fetchAll({ commit, dispatch }, filter = {}) {
      commit('request')
      commit('setActiveFilter', { filter })

      const promises = [
        dispatch('fetchKpi', filter),
        dispatch('fetchMemberGrowth', filter),
        dispatch('fetchChannelRanking', filter),
        dispatch('fetchPointActivity', filter),
        dispatch('fetchTierDistribution', filter),
        dispatch('fetchTopProducts', filter),
        dispatch('fetchTopVouchers', filter),
        dispatch('fetchMembersAtRisk', filter),
      ]

      return Promise.allSettled(promises).then(() => {
        commit('success')
      })
    },

    fetchKpi({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/kpi`, buildParams(filter))
          .then((result) => {
            commit('setKpi', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load KPI' })
            reject(err)
          })
      })
    },

    fetchMemberGrowth({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/member-growth`, buildParams(filter))
          .then((result) => {
            commit('setMemberGrowth', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load member growth' })
            reject(err)
          })
      })
    },

    fetchChannelRanking({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/channel-ranking`, buildParams(filter))
          .then((result) => {
            commit('setChannelRanking', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load channel ranking' })
            reject(err)
          })
      })
    },

    fetchPointActivity({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/point-activity`, buildParams(filter))
          .then((result) => {
            commit('setPointActivity', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load point activity' })
            reject(err)
          })
      })
    },

    fetchTierDistribution({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/tier-distribution`, buildParams(filter))
          .then((result) => {
            commit('setTierDistribution', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load tier distribution' })
            reject(err)
          })
      })
    },

    fetchTopProducts({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/top-products`, buildParams(filter))
          .then((result) => {
            commit('setTopProducts', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load top products' })
            reject(err)
          })
      })
    },

    fetchTopVouchers({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/top-vouchers`, buildParams(filter))
          .then((result) => {
            commit('setTopVouchers', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load top vouchers' })
            reject(err)
          })
      })
    },

    fetchMembersAtRisk({ commit }, filter = {}) {
      return new Promise((resolve, reject) => {
        api.request('get', `${BASE_URL}/members-at-risk`, buildParams(filter))
          .then((result) => {
            commit('setMembersAtRisk', { data: result.data.data })
            resolve(result)
          })
          .catch((err) => {
            commit('error', { message: err?.response?.data?.message || 'Failed to load members at risk' })
            reject(err)
          })
      })
    },
  },

  getters: {
    isLoading: (state) => state.status === 'loading',
    hasError:  (state) => state.status === 'error',
    kpi:       (state) => state.kpi,
    memberGrowth:     (state) => state.memberGrowth,
    channelRanking:   (state) => state.channelRanking,
    pointActivity:    (state) => state.pointActivity,
    tierDistribution: (state) => state.tierDistribution,
    topProducts:      (state) => state.topProducts,
    topVouchers:      (state) => state.topVouchers,
    membersAtRisk:    (state) => state.membersAtRisk,
    activeFilter:     (state) => state.activeFilter,
  },
}

/**
 * Build query params sesuai format yang diterima backend:
 * filter[timeframe]=this_month&filter[corporate_id]=xxx
 */
function buildParams(filter = {}) {
  const filterParams = {}
  if (filter.timeframe)    filterParams.timeframe    = filter.timeframe
  if (filter.corporate_id) filterParams.corporate_id = filter.corporate_id
  if (filter.branch_id)    filterParams.branch_id    = filter.branch_id
  if (filter.merchant_id)  filterParams.merchant_id  = filter.merchant_id

  // Inject current timezone automatically
  filterParams.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  filterParams.timezone_offset = -(new Date().getTimezoneOffset())
  
  return { filter: filterParams }
}
